import { createFileRoute } from '@tanstack/react-router';
import { Trans, Plural } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import PageHeader from '../components/PageHeader';
import BookmarkList from '../components/BookmarkList';
import { BookmarkSelectionProvider } from '../contexts/BookmarkSelectionContext';
import { useExitBatchModeOnNavigate } from '../hooks/useExitBatchModeOnNavigate';
import { useUserPreferences } from '../hooks/useUserPreferences';
import { useCurrentUser } from '../services/userHooks';
import { useSearch } from '../services/search';
import { useState } from 'react';

export const Route = createFileRoute('/_app/search')({
  component: SearchPage,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      q: (search.q as string) || '',
      site: (search.site as string) || '',
      author: (search.author as string) || '',
      sort: (search.sort as 'relevance' | 'oldest' | 'newest') || 'relevance',
      page: search.page ? parseInt(search.page as string) || 1 : 1,
    };
  },
});

function BatchModeNavigationGuard() {
  useExitBatchModeOnNavigate();
  return null;
}

function SearchPage() {
  useLingui();
  const { q: query, site, author, sort, page } = Route.useSearch();
  const [error, setError] = useState<string | null>(null);

  const { currentUser } = useCurrentUser();
  const { preferences } = useUserPreferences();

  // Use server-side search if user is a subscriber and has query
  const hasSearchTerms = Boolean(query || site || author);
  const shouldUseServerSearch = currentUser?.isSubscriber && hasSearchTerms;

  const {
    data: searchData,
    isLoading: isSearchLoading,
    error: searchError,
  } = useSearch(
    { q: query, site, author, sort, page },
    { enabled: shouldUseServerSearch },
  );

  // Handle search errors
  const handleSearchError = () => {
    // TODO(Drew subscription): Re-enable premium check after testing
    if (searchError?.message === 'PREMIUM_REQUIRED') {
      setError(t`Premium subscription required`);
    } else {
      setError(t`Sorry, there was an error. Please try again.`);
    }
  };

  // Set error when search fails
  if (searchError && !error) {
    handleSearchError();
  }

  // TODO(Drew subscription): Re-enable non-subscriber check after testing
  // Handle non-subscribers
  if (!currentUser?.isSubscriber) {
    return (
      <div className="px-section-x py-section-y">
        <h2 className="mb-4 text-lg font-medium">
          <Trans>Search</Trans>
        </h2>
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-amber-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-amber-800">
                <Trans>Upgrade to Premium</Trans>
              </h3>
              <div className="mt-2 text-sm text-amber-700">
                <p>
                  <Trans>
                    Get full-text search, unlimited notes, text-to-speech playlists, and
                    more with Instapaper Premium.
                  </Trans>
                </p>
              </div>
              <div className="mt-4">
                <a
                  href="/subscription"
                  className="text-sm font-medium text-amber-800 hover:text-amber-600"
                >
                  <Trans>Learn more about Premium →</Trans>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Handle no search terms
  if (!hasSearchTerms) {
    return (
      <div className="px-section-x py-section-y">
        <h2 className="mb-4 text-lg font-medium">
          <Trans>Search</Trans>
        </h2>
        <p className="text-gray-600">
          <Trans>Enter a search term to find articles in your library.</Trans>
        </p>
      </div>
    );
  }

  // Handle search errors
  if (error) {
    return (
      <div className="px-section-x py-section-y">
        <h2 className="mb-4 text-lg font-medium">
          <Trans>Search Error</Trans>
        </h2>
        <div className="border-danger bg-surface-danger-light rounded-lg border p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="text-danger h-5 w-5"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-danger text-sm font-medium">
                <Trans>Search Error</Trans>
              </h3>
              <div className="text-danger mt-2 text-sm">
                <p>{error}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const bookmarks = searchData?.bookmarks || [];
  const searchTitle = query || site || author;

  return (
    <BookmarkSelectionProvider>
      <BatchModeNavigationGuard />
      {/* Page Header with search bar */}
      <PageHeader mode="unread" />

      {/* Search Results Summary - Now at the top */}
      <div className="px-section-x py-section-y">
        <h2 className="text-lg font-medium">
          <Trans>Search results for "{searchTitle}"</Trans>
        </h2>
        {searchData && (
          <p className="mt-1 text-sm text-gray-500">
            <Plural
              value={searchData.count}
              one="# result found"
              other="# results found"
            />
            {sort !== 'relevance' && ` • ${t`Sorted by ${sort}`}`}
          </p>
        )}
      </div>

      {/* Bookmark List */}
      <BookmarkList
        bookmarks={bookmarks}
        condensedView={preferences.bookmarkList.view === 'condensed'}
        showThumbnails={preferences.bookmarkList.thumbnails}
        isLoading={isSearchLoading}
        mode="unread"
      />
    </BookmarkSelectionProvider>
  );
}
